import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { RealtimeProvider } from "@/context/RealtimeContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import { Toaster } from "@/components/ui/sonner";
import { ActionBlockedHost } from "@/components/ActionBlockedDialog";
import { ChangePasswordCard } from "@/components/ChangePasswordCard";

// Route-level code splitting: pages load on demand so the initial bundle stays small.
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Projects = lazy(() => import("@/pages/Projects"));
const ProjectDetail = lazy(() => import("@/pages/ProjectDetail"));
const Leads = lazy(() => import("@/pages/Leads"));
const LeadDetail = lazy(() => import("@/pages/LeadDetail"));
const AttentionCollection = lazy(() => import("@/pages/AttentionCollection"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const Team = lazy(() => import("@/pages/Team"));
const AuditLog = lazy(() => import("@/pages/AuditLog"));
const Policies = lazy(() => import("@/pages/Policies"));
const Permissions = lazy(() => import("@/pages/Permissions"));
const Settings = lazy(() => import("@/pages/Settings"));
const Backup = lazy(() => import("@/pages/Backup"));
const Performance = lazy(() => import("@/pages/Performance"));
const FinanceOverview = lazy(() => import("@/pages/FinanceOverview"));

const PageFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground text-sm" data-testid="page-loading">Loading…</div>
);

// Remount detail pages per record so no state from the previous record is shown.
function KeyedById({ C }) {
  const { id } = useParams();
  return <C key={id} />;
}


function ForcedChangePassword() {
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-lg" data-testid="forced-change-password">
        <h1 className="font-display text-xl font-bold mb-1">Set a new password</h1>
        <p className="text-sm text-muted-foreground mb-4">Welcome! Choose your own password to continue.</p>
        <ChangePasswordCard forced />
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user, ready, mustChangePassword, impersonating } = useAuth();
  if (!ready) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (mustChangePassword && !impersonating) return <ForcedChangePassword />;
  return <Layout>{children}</Layout>;
}

function SidebarGate({ keyName, manager, permsNav, backup, children }) {
  const { sidebar, isManager, landingPath, user } = useAuth();
  if (backup && !user?.full_access) return <Navigate to={landingPath()} replace />;
  if (manager && !isManager) return <Navigate to={landingPath()} replace />;
  if (permsNav && !isManager && sidebar("permissions") === "none") return <Navigate to={landingPath()} replace />;
  if (keyName && sidebar(keyName) === "none") return <Navigate to={landingPath()} replace />;
  return children;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageFallback />}>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/performance" element={<Protected><Performance /></Protected>} />
      <Route path="/finance" element={<Protected><SidebarGate keyName="finance"><FinanceOverview /></SidebarGate></Protected>} />
      <Route path="/settings" element={<Protected><SidebarGate backup><Settings /></SidebarGate></Protected>} />
      <Route path="/backup" element={<Protected><SidebarGate backup><Backup /></SidebarGate></Protected>} />
      <Route path="/projects" element={<Protected><SidebarGate keyName="projects"><Projects /></SidebarGate></Protected>} />
      <Route path="/projects/:id" element={<Protected><KeyedById C={ProjectDetail} /></Protected>} />
      <Route path="/leads" element={<Protected><SidebarGate keyName="leads"><Leads /></SidebarGate></Protected>} />
      <Route path="/leads/:id" element={<Protected><KeyedById C={LeadDetail} /></Protected>} />
      <Route path="/attention" element={<Protected><SidebarGate keyName="attention"><AttentionCollection /></SidebarGate></Protected>} />
      <Route path="/notifications" element={<Protected><SidebarGate keyName="notifications"><Notifications /></SidebarGate></Protected>} />
      <Route path="/team" element={<Protected><SidebarGate keyName="team"><Team /></SidebarGate></Protected>} />
      <Route path="/permissions" element={<Protected><SidebarGate permsNav><Permissions /></SidebarGate></Protected>} />
      <Route path="/policies" element={<Protected><SidebarGate keyName="policies"><Policies /></SidebarGate></Protected>} />
      <Route path="/audit" element={<Protected><SidebarGate keyName="audit"><AuditLog /></SidebarGate></Protected>} />
      <Route path="*" element={<Protected><Navigate to="/" replace /></Protected>} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <RealtimeProvider>
              <AppRoutes />
            </RealtimeProvider>
          </AuthProvider>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        <ActionBlockedHost />
      </div>
    </ThemeProvider>
  );
}

export default App;
